import { supabase } from '../lib/supabase';
import { sendCoachRegistrationInviteEmail, sendCoachStatusNotificationEmail } from './emailService';
import { calculateLicenseExpirationDate, formatValidityMonthsLabel, parseValidityMonths } from '../utils/coachLicense';
import { generateCoachLicenseNumber, resolveCoachLicenseNumber } from '../utils/coachLicenseNumber';
import { getNationalityLabel } from '../data/nationalities';
import { getRegionLabel } from '../utils/tanzaniaRegions';
import { getAppPath } from '../utils/appUrl';

const BUCKET_NAME = 'coach-documents';

const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('localhost:3001')) {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
    return url.replace(/https?:\/\/localhost:3001/, apiUrl);
  }
  if (url.startsWith('http://') && !url.startsWith('http://localhost')) {
    return url.replace(/^http:\/\//, 'https://');
  }
  return url;
};

const getFileUrl = (filePath) => {
  if (!filePath) return null;
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return normalizeUrl(filePath);
  }
  const { data: { publicUrl } } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);
  return publicUrl;
};

const normalizeAssignment = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    coachId: row.coach_id,
    teamId: row.team_id,
    regionalTeamId: row.regional_team_id,
    roleTitle: row.role_title,
    isPrimary: row.is_primary,
    assignedAt: row.assigned_at,
    team: row.team
      ? { id: row.team.id, name: row.team.name || row.team.team_name }
      : null,
    regionalTeam: row.regional_team
      ? { id: row.regional_team.id, name: row.regional_team.team_name, region: row.regional_team.region }
      : null,
  };
};

const normalizeCoach = (row) => {
  if (!row) return null;
  const documents = (row.coach_documents || []).map((doc) => ({
    id: doc.id,
    documentType: doc.document_type,
    fileName: doc.file_name,
    filePath: doc.file_path,
    fileUrl: doc.file_url ? normalizeUrl(doc.file_url) : getFileUrl(doc.file_path),
    fileSize: doc.file_size,
    createdAt: doc.created_at,
  }));

  const certificates = documents.filter((doc) => doc.documentType === 'coaching_certificate');
  const certificate = certificates[0] || null;
  const photo = documents.find((doc) => doc.documentType === 'coach_photo');
  const passport = documents.find((doc) => doc.documentType === 'passport');

  return {
    id: row.id,
    coachName: row.coach_name,
    grade: row.grade,
    region: row.region,
    nationality: row.nationality,
    gender: row.gender,
    mobile: row.mobile,
    email: row.email,
    physicalAddress: row.physical_address,
    dateOfBirth: row.date_of_birth,
    licenseNumber: row.license_number,
    licenseStatus: row.license_status,
    validityPeriod: row.validity_period,
    licenseExpirationDate: row.license_expiration_date,
    registrationStatus: row.registration_status,
    approvedBy: row.approved_by,
    approvedDate: row.approved_date,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    documents,
    coachingCertificates: certificates,
    coachingCertificate: certificate || null,
    coachPhoto: photo || null,
    passport: passport || null,
    teamAssignments: (row.coach_team_assignments || []).map(normalizeAssignment),
  };
};

const buildCoachEmailData = (coach) => ({
  coachName: coach.coachName,
  grade: coach.grade,
  region: getRegionLabel(coach.region) || coach.region,
  nationality: getNationalityLabel(coach.nationality) || coach.nationality,
  gender: coach.gender,
  mobile: coach.mobile,
  email: coach.email,
  physicalAddress: coach.physicalAddress,
  dateOfBirth: coach.dateOfBirth,
  licenseNumber: coach.licenseNumber,
  licenseStatus: coach.licenseStatus,
  registrationStatus: coach.registrationStatus,
  validityPeriod: formatValidityMonthsLabel(coach.validityPeriod),
  licenseExpirationDate: coach.licenseExpirationDate,
  photoUrl: coach.coachPhoto?.fileUrl || null,
  passportUrl: coach.passport?.fileUrl || null,
  certificateUrls: (coach.coachingCertificates || [])
    .filter((cert) => cert.fileUrl)
    .map((cert, index) => ({
      label: cert.fileName || `Coaching Certificate ${index + 1}`,
      url: cert.fileUrl,
    })),
  profileUrl:
    typeof window !== 'undefined' && coach.id
      ? `${window.location.origin}/league-coaches?coach=${coach.id}`
      : null,
});

export { generateCoachLicenseNumber, resolveCoachLicenseNumber } from '../utils/coachLicenseNumber';

export const coachService = {
  getFileUrl,

  async getAll() {
    const { data, error } = await supabase
      .from('coaches')
      .select(`
        *,
        coach_documents(id, document_type, file_name, file_path, file_url, file_size, created_at),
        coach_team_assignments(
          id, coach_id, team_id, regional_team_id, role_title, is_primary, assigned_at,
          team:teams(id, name),
          regional_team:regional_teams(id, team_name, region)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message || 'Failed to load coaches');
    return (data || []).map(normalizeCoach);
  },

  async getById(coachId) {
    const { data, error } = await supabase
      .from('coaches')
      .select(`
        *,
        coach_documents(id, document_type, file_name, file_path, file_url, file_size, created_at),
        coach_team_assignments(
          id, coach_id, team_id, regional_team_id, role_title, is_primary, assigned_at,
          team:teams(id, name),
          regional_team:regional_teams(id, team_name, region)
        )
      `)
      .eq('id', coachId)
      .single();

    if (error) throw new Error(error.message || 'Failed to load coach');
    return normalizeCoach(data);
  },

  async uploadFile(file, coachId, documentType = 'coaching_certificate') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!coachId) throw new Error('Coach ID is required for file upload');

    const isPhoto = documentType === 'coach_photo';
    const allowedMimes = isPhoto
      ? ['image/jpeg', 'image/jpg', 'image/png']
      : [
          'application/pdf',
          'image/jpeg', 'image/jpg', 'image/png',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];
    if (!allowedMimes.includes(file.type)) {
      const label = isPhoto
        ? 'Coach photo'
        : documentType === 'passport'
          ? 'Passport'
          : 'Certificate';
      throw new Error(
        isPhoto
          ? 'Coach photo must be JPG or PNG'
          : `${label} must be PDF, image, or Word document`
      );
    }
    const maxSize = isPhoto ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`File size must be less than ${isPhoto ? '5MB' : '10MB'}`);
    }

    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 11);
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const ext = sanitizedName.split('.').pop();
    const nameWithoutExt = sanitizedName.substring(0, sanitizedName.lastIndexOf('.')) || sanitizedName;
    const filename = `${timestamp}-${randomStr}-${nameWithoutExt}.${ext}`;
    const folder = isPhoto ? 'photo' : 'documents';
    const filePath = `coaches/${coachId}/${folder}/${filename}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      if (uploadError.message?.includes('Bucket not found')) {
        throw new Error('Storage bucket "coach-documents" not found. Please create it in Supabase Storage.');
      }
      throw new Error(uploadError.message || 'Failed to upload certificate');
    }

    const { data: { publicUrl } } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);

    return {
      filePath,
      fileUrl: publicUrl,
      fileName: file.name,
      fileSize: file.size,
    };
  },

  async saveDocument(coachId, documentData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coach_documents')
      .insert({
        coach_id: coachId,
        document_type: documentData.documentType || 'coaching_certificate',
        file_name: documentData.fileName,
        file_size: documentData.fileSize,
        file_path: documentData.filePath,
        file_url: documentData.fileUrl,
        uploaded_by: user.id,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to save coach document');
    return data;
  },

  async deleteDocument(documentId) {
    const { data: doc, error: fetchError } = await supabase
      .from('coach_documents')
      .select('id, file_path')
      .eq('id', documentId)
      .single();

    if (fetchError) throw new Error(fetchError.message || 'Failed to load coach document');

    if (doc?.file_path) {
      await this.deleteFile(doc.file_path);
    }

    const { error } = await supabase.from('coach_documents').delete().eq('id', documentId);
    if (error) throw new Error(error.message || 'Failed to delete coach document');
  },

  async saveUploadedFile(coachId, file, documentType) {
    if (!file) return null;
    const uploadResult = await this.uploadFile(file, coachId, documentType);
    await this.saveDocument(coachId, {
      documentType,
      fileName: uploadResult.fileName,
      filePath: uploadResult.filePath,
      fileUrl: uploadResult.fileUrl,
      fileSize: uploadResult.fileSize,
    });
    return uploadResult;
  },

  async syncCoachDocuments(coachId, coachData, existingCoach = null) {
    const removedCertificateIds = coachData.removedCertificateIds || [];
    for (const documentId of removedCertificateIds) {
      await this.deleteDocument(documentId);
    }

    const certificateFiles = [
      ...(coachData.certificateFiles || []),
      ...(coachData.certificateFile ? [coachData.certificateFile] : []),
    ];
    for (const file of certificateFiles) {
      await this.saveUploadedFile(coachId, file, 'coaching_certificate');
    }

    if (coachData.passportFile) {
      if (existingCoach?.passport?.id) {
        await this.deleteDocument(existingCoach.passport.id);
      }
      await this.saveUploadedFile(coachId, coachData.passportFile, 'passport');
    }

    if (coachData.photoFile) {
      if (existingCoach?.coachPhoto?.id) {
        await this.deleteDocument(existingCoach.coachPhoto.id);
      }
      await this.saveUploadedFile(coachId, coachData.photoFile, 'coach_photo');
    }
  },

  async deleteFile(filePath) {
    if (!filePath) return;
    let storagePath = filePath;
    if (filePath.startsWith('http')) {
      const match = filePath.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/);
      if (match) storagePath = match[1];
      else return;
    }
    await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
  },

  async assignToTeam(coachId, assignment) {
    const payload = {
      coach_id: coachId,
      role_title: assignment.roleTitle || 'Head Coach',
      is_primary: assignment.isPrimary === true,
    };

    if (assignment.teamType === 'regional') {
      payload.regional_team_id = assignment.teamId;
      payload.team_id = null;
    } else {
      payload.team_id = assignment.teamId;
      payload.regional_team_id = null;
    }

    const { data, error } = await supabase
      .from('coach_team_assignments')
      .insert(payload)
      .select(`
        *,
        team:teams(id, name),
        regional_team:regional_teams(id, team_name, region)
      `)
      .single();

    if (error) throw new Error(error.message || 'Failed to assign coach to team');
    return normalizeAssignment(data);
  },

  async removeTeamAssignment(assignmentId) {
    const { error } = await supabase
      .from('coach_team_assignments')
      .delete()
      .eq('id', assignmentId);
    if (error) throw new Error(error.message || 'Failed to remove team assignment');
  },

  async syncTeamAssignments(coachId, assignments = []) {
    const { error: deleteError } = await supabase
      .from('coach_team_assignments')
      .delete()
      .eq('coach_id', coachId);
    if (deleteError) throw new Error(deleteError.message || 'Failed to update team assignments');

    for (const assignment of assignments) {
      if (!assignment?.teamId) continue;
      await this.assignToTeam(coachId, assignment);
    }
  },

  async registerCoach(coachData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('coaches')
      .insert({
        coach_name: coachData.coachName?.trim(),
        grade: coachData.grade,
        region: coachData.region,
        nationality: coachData.nationality,
        gender: coachData.gender,
        mobile: coachData.mobile?.trim() || null,
        email: coachData.email?.trim() || null,
        physical_address: coachData.physicalAddress?.trim() || null,
        date_of_birth: coachData.dateOfBirth || null,
        license_number: resolveCoachLicenseNumber(coachData.licenseNumber, coachData.grade),
        license_status: coachData.licenseStatus || 'pending',
        validity_period: String(parseValidityMonths(coachData.validityPeriod)),
        license_expiration_date: calculateLicenseExpirationDate(coachData.validityPeriod),
        registration_status: 'submitted',
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to register coach');

    try {
      await this.syncCoachDocuments(data.id, coachData);
    } catch (uploadError) {
      console.error('Coach document upload failed:', uploadError);
    }

    if (coachData.teamAssignments?.length) {
      await this.syncTeamAssignments(data.id, coachData.teamAssignments);
    }

    return this.getById(data.id);
  },

  async update(coachId, coachData) {
    const payload = { updated_at: new Date().toISOString() };
    let existingCoach = null;
    let previousLicenseStatus = null;

    const getExistingCoach = async () => {
      if (!existingCoach) {
        existingCoach = await this.getById(coachId);
      }
      return existingCoach;
    };

    if (coachData.coachName !== undefined) payload.coach_name = coachData.coachName?.trim();
    if (coachData.grade !== undefined) payload.grade = coachData.grade;
    if (coachData.region !== undefined) payload.region = coachData.region;
    if (coachData.nationality !== undefined) payload.nationality = coachData.nationality;
    if (coachData.gender !== undefined) payload.gender = coachData.gender;
    if (coachData.mobile !== undefined) payload.mobile = coachData.mobile?.trim() || null;
    if (coachData.email !== undefined) payload.email = coachData.email?.trim() || null;
    if (coachData.physicalAddress !== undefined) payload.physical_address = coachData.physicalAddress?.trim() || null;
    if (coachData.dateOfBirth !== undefined) payload.date_of_birth = coachData.dateOfBirth || null;
    if (coachData.licenseStatus !== undefined) {
      const existing = await getExistingCoach();
      previousLicenseStatus = existing?.licenseStatus;
      payload.license_status = coachData.licenseStatus;
    }
    if (coachData.validityPeriod !== undefined) {
      payload.validity_period = String(parseValidityMonths(coachData.validityPeriod));
      payload.license_expiration_date = calculateLicenseExpirationDate(coachData.validityPeriod);
    } else if (coachData.licenseExpirationDate !== undefined) {
      payload.license_expiration_date = coachData.licenseExpirationDate || null;
    }

    if (coachData.grade !== undefined || coachData.licenseNumber !== undefined) {
      const existing = await getExistingCoach();
      const nextGrade = coachData.grade ?? existing?.grade;
      const submittedLicense = coachData.licenseNumber !== undefined
        ? coachData.licenseNumber
        : existing?.licenseNumber;
      payload.license_number = resolveCoachLicenseNumber(submittedLicense, nextGrade);
    }

    const { error } = await supabase.from('coaches').update(payload).eq('id', coachId);
    if (error) throw new Error(error.message || 'Failed to update coach');

    await getExistingCoach();
    try {
      await this.syncCoachDocuments(coachId, coachData, existingCoach);
    } catch (uploadError) {
      console.error('Coach document upload failed:', uploadError);
      throw uploadError;
    }

    if (coachData.teamAssignments !== undefined) {
      await this.syncTeamAssignments(coachId, coachData.teamAssignments);
    }

    const updatedCoach = await this.getById(coachId);

    if (coachData.licenseStatus !== undefined && previousLicenseStatus !== coachData.licenseStatus) {
      if (coachData.licenseStatus === 'suspended') {
        this.notifyStatusChange(updatedCoach, 'suspended').catch((err) => {
          console.error('Coach suspension notification failed:', err);
        });
      } else if (coachData.licenseStatus === 'active' && previousLicenseStatus === 'suspended') {
        this.notifyStatusChange(updatedCoach, 'reactivated').catch((err) => {
          console.error('Coach reactivation notification failed:', err);
        });
      }
    }

    return updatedCoach;
  },

  async notifyStatusChange(coachOrId, action) {
    const coach =
      typeof coachOrId === 'string' ? await this.getById(coachOrId) : coachOrId;

    if (!coach?.email?.trim()) {
      return null;
    }

    return sendCoachStatusNotificationEmail({
      to: coach.email.trim(),
      coachName: coach.coachName,
      action,
      coachData: buildCoachEmailData(coach),
    });
  },

  async delete(coachId) {
    const coach = await this.getById(coachId);
    for (const doc of coach?.documents || []) {
      if (doc.filePath) {
        await this.deleteFile(doc.filePath);
      }
    }
    const { error } = await supabase.from('coaches').delete().eq('id', coachId);
    if (error) throw new Error(error.message || 'Failed to delete coach');
  },

  async validateSelfRegistrationToken(token) {
    if (token === 'public') {
      return { valid: true, public: true };
    }
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
    const response = await fetch(`${apiUrl}/coach-self-registration/validate/${encodeURIComponent(token)}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'This registration link is invalid or expired.');
    }
    return data;
  },

  getPublicRegistrationUrl() {
    return getAppPath('/coach-register/public');
  },

  async checkEmailAvailability(email) {
    const normalized = email?.trim()?.toLowerCase();
    if (!normalized) {
      return { available: null, message: '' };
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
    const response = await fetch(
      `${apiUrl}/coach-self-registration/check-email/${encodeURIComponent(normalized)}`
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to verify email availability');
    }
    return data;
  },

  async submitSelfRegistration(token, formData) {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.tanzaniabasketball.com';
    const payload = new FormData();
    if (token && token !== 'public') {
      payload.append('token', token);
    }
    payload.append('coachName', formData.coachName || '');
    payload.append('grade', formData.grade || '');
    payload.append('region', formData.region || '');
    payload.append('nationality', formData.nationality || '');
    payload.append('gender', formData.gender || '');
    payload.append('mobile', formData.mobile || '');
    payload.append('email', formData.email || '');
    payload.append('physicalAddress', formData.physicalAddress || '');
    payload.append('dateOfBirth', formData.dateOfBirth || '');
    payload.append('licenseNumber', formData.licenseNumber || '');
    payload.append('validityPeriod', String(parseValidityMonths(formData.validityPeriod)));
    (formData.certificateFiles || []).forEach((file) => {
      payload.append('certificateFiles', file);
    });
    if (formData.certificateFile) payload.append('certificateFiles', formData.certificateFile);
    if (formData.passportFile) payload.append('passportFile', formData.passportFile);
    if (formData.photoFile) payload.append('photoFile', formData.photoFile);

    const endpoint =
      token === 'public'
        ? `${apiUrl}/coach-self-registration/public/submit`
        : `${apiUrl}/coach-self-registration/submit`;

    const response = await fetch(endpoint, {
      method: 'POST',
      body: payload,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to submit coach registration');
    }
    return data;
  },

  async createSelfRegistrationInvite({ email, coachName, expiresInDays = 7 }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const normalizedEmail = email?.trim()?.toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error('A valid email address is required');
    }

    const emailCheck = await this.checkEmailAvailability(normalizedEmail);
    if (emailCheck.available === false) {
      throw new Error(emailCheck.message || 'This email is already registered to a coach.');
    }

    const token = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + Number(expiresInDays || 7));

    const { data, error } = await supabase
      .from('coach_registration_invites')
      .insert({
        token,
        email: normalizedEmail,
        coach_name: coachName?.trim() || null,
        expires_at: expiresAt.toISOString(),
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to create registration invite');

    const registrationUrl = `${window.location.origin}/coach-register/${token}`;

    await sendCoachRegistrationInviteEmail({
      to: normalizedEmail,
      coachName: coachName?.trim() || null,
      registrationUrl,
      expiresAt: expiresAt.toISOString(),
    });

    return {
      ...data,
      registrationUrl,
      expiresAt: expiresAt.toISOString(),
    };
  },
};
